import { Component, Input, OnChanges, SimpleChanges, input, inject, DestroyRef } from '@angular/core';
import { Employee } from '../../../classes/models/employee/Employee';
import { CapabilityView } from '../../../classes/models/employee/CapabilityView';
import { CapabilityService } from '../../../services/http/capability.service';
import { CapabilityViewType } from '../../../classes/enums/CapabilityViewType';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SHARED_IMPORTS } from '../../../shared/standalone-imports';

/**
 * This component displays a calendar of capabilities for the store and available employees to fulfill a specific role.
 */
@Component({
    selector: 'app-employees-capabilities',
    templateUrl: './employees-capabilities.component.html',
    styleUrls: ['./employees-capabilities.component.less'],
    imports: [SHARED_IMPORTS]
})
export class EmployeesCapabilitiesComponent implements OnChanges {
  private capabilityService = inject(CapabilityService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** The identifier of a store. */
  readonly storeId = input<string>(undefined);
  /** A list of all employees of the selected store. */
  @Input() employees: Employee[];
  /** A Monday of the week, for which capabilities are displayed. */
  @Input() selectedMonday: string;
  /** A list of each date in a week. */
  @Input() dates: string[];

  /** A list of capability views for the selected week. */
  capabilityViews: CapabilityView[] = [];


  /**
   * Updates a map of capabilities on any change of input data.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedMonday && changes.selectedMonday.currentValue !== changes.selectedMonday.previousValue) {
      this.capabilityService.getCapabilityViews(this.storeId(), this.selectedMonday)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: capabilityViews => this.capabilityViews = capabilityViews
        });
    }
  }

  /**
   * Gets sorted capabilities from earliest to latest on a specific day.
   * @param date the date for which capabilities are sorted.
   */
  getSortedTimes(date: string): CapabilityView[] {
    const filtered = this.capabilityViews.filter(item => item.date === date);
    if (!filtered) {
      return [];
    }
    return filtered
      .sort((first, second) => first.startTime > second.startTime ? 1 : -1);
  }

  /**
   * Finds available employees.
   * @param capabilityView details of a capability.
   */
  findAvailableEmployees(capabilityView: CapabilityView): Employee[] {
    return this.employees.filter(employee => capabilityView.availableEmployees.find(id => id.value === employee.employeePositionId.value));
  }

  /**
   * Checks if a capability happens all day.
   * @param capabilityView details of a capability.
   */
  happensAllDay(capabilityView: CapabilityView): boolean {
    return capabilityView.startTime === null && capabilityView.endTime === null;
  }

  /**
   * Checks if there are no available employees for a capability.
   * @param capabilityView details of a capability.
   */
  hasNoEmployees(capabilityView: CapabilityView): boolean {
    return capabilityView.availableEmployees.length === 0;
  }

  /**
   * Checks if capability's type is keyholder.
   * @param capabilityView details of a capability.
   */
  isKeyholder(capabilityView: CapabilityView): boolean {
    return capabilityView.requiredCapability === CapabilityViewType.KEY_HOLDER;
  }

  /**
   * Checks if capability's type is banking.
   * @param capabilityView details of a capability.
   */
  isBanking(capabilityView: CapabilityView): boolean {
    return capabilityView.requiredCapability === CapabilityViewType.BANKING;
  }

  /**
   * Checks if capability's type is furniture changeover.
   * @param capabilityView details of a capability.
   */
  isChangeover(capabilityView: CapabilityView): boolean {
    return capabilityView.requiredCapability === CapabilityViewType.FURNITURE_CHANGEOVER;
  }

  /**
   * Checks if capability's type is salesperson.
   * @param capabilityView details of a capability.
   */
  isSalesPerson(capabilityView: CapabilityView): boolean {
    return capabilityView.requiredCapability === CapabilityViewType.SALESPERSON;
  }

  /**
   * Checks if capability's type is shop opener.
   * @param capabilityView details of a capability.
   */
  isShopOpener(capabilityView: CapabilityView): boolean {
    return capabilityView.requiredCapability === CapabilityViewType.SHOP_OPENER;
  }

  /**
   * Checks if capability's type is shop closer.
   * @param capabilityView details of a capability.
   */
  isShopCloser(capabilityView: CapabilityView): boolean {
    return capabilityView.requiredCapability === CapabilityViewType.SHOP_CLOSER;
  }

  /**
   * Checks if capability's type is changeover manager.
   * @param capabilityView details of a capability.
   */
  isChangeoverManager(capabilityView: CapabilityView): boolean {
    return capabilityView.requiredCapability === CapabilityViewType.CHANGEOVER_MANAGER;
  }
}

